import React from 'react';
import { alertService } from '../services/alertService';
import { confirmationModals } from './ConfirmationModals';
import { 
  AlertCircle, 
  CheckCircle, 
  Info, 
  HelpCircle, 
  Trash2, 
  Save, 
  Send,
  Download,
  Upload,
  Settings
} from 'lucide-react';

const SweetAlertDemo = () => {
  // Fonctions de démonstration
  const showSuccessAlert = async () => {
    await alertService.success(
      'Opération réussie !',
      'Votre action a été effectuée avec succès.'
    );
  };

  const showErrorAlert = async () => {
    await alertService.error(
      'Erreur !',
      'Une erreur s\'est produite lors de l\'exécution de votre demande.'
    );
  };

  const showWarningAlert = async () => {
    await alertService.warning(
      'Attention !',
      'Cette action peut avoir des conséquences importantes.'
    );
  };

  const showInfoAlert = async () => {
    await alertService.info(
      'Information',
      'Voici des informations importantes concernant votre compte.'
    );
  };

  const showQuestionAlert = async () => {
    await alertService.question(
      'Question',
      'Avez-vous des questions concernant cette fonctionnalité ?'
    );
  };

  const showConfirmDialog = async () => {
    const result = await alertService.confirm(
      'Confirmation',
      'Voulez-vous vraiment effectuer cette action ?',
      {
        confirmButtonText: 'Oui, continuer',
        cancelButtonText: 'Non, annuler'
      }
    );

    if (result.isConfirmed) {
      await alertService.success('Action confirmée !');
    } else {
      await alertService.info('Action annulée');
    }
  };

  const showDeleteConfirm = async () => {
    const result = await confirmationModals.deleteContact('John Doe');
    
    if (result.isConfirmed) {
      await alertService.success('Contact supprimé !');
    }
  };

  const showSaveConfirm = async () => {
    const result = await confirmationModals.saveSettings();
    
    if (result.isConfirmed) {
      await alertService.success('Paramètres sauvegardés !');
    }
  };

  const showCustomModal = async () => {
    await alertService.custom({
      title: 'Modal personnalisée',
      html: `
        <div style="text-align: left;">
          <p>Voici une modal personnalisée avec du contenu HTML :</p>
          <ul>
            <li>Support du HTML</li>
            <li>Styles personnalisés</li>
            <li>Contenu dynamique</li>
          </ul>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Compris',
      cancelButtonText: 'Fermer'
    });
  };

  const showFormModal = async () => {
    const result = await alertService.formAlert(
      'Formulaire de contact',
      `
        <form id="contactForm">
          <div class="form-group">
            <label for="name">Nom :</label>
            <input type="text" id="name" class="swal2-input" placeholder="Votre nom" required>
          </div>
          <div class="form-group">
            <label for="email">Email :</label>
            <input type="email" id="email" class="swal2-input" placeholder="votre@email.com" required>
          </div>
          <div class="form-group">
            <label for="message">Message :</label>
            <textarea id="message" class="swal2-textarea" placeholder="Votre message" required></textarea>
          </div>
        </form>
      `,
      {
        preConfirm: () => {
          const name = document.getElementById('name').value;
          const email = document.getElementById('email').value;
          const message = document.getElementById('message').value;
          
          if (!name || !email || !message) {
            alertService.error('Erreur', 'Veuillez remplir tous les champs');
            return false;
          }
          
          return { name, email, message };
        }
      }
    );

    if (result.isConfirmed) {
      await alertService.success(
        'Message envoyé !',
        `Merci ${result.value.name}, votre message a été envoyé.`
      );
    }
  };

  const showLoadingModal = async () => {
    const loadingSwal = alertService.loading('Traitement en cours...', 'Veuillez patienter');
    
    // Simuler un traitement
    setTimeout(async () => {
      alertService.close();
      await alertService.success('Traitement terminé !');
    }, 3000);
  };

  const showToastSuccess = async () => {
    await alertService.toastSuccess('Opération réussie !');
  };

  const showToastError = async () => {
    await alertService.toastError('Erreur !');
  };

  const showToastInfo = async () => {
    await alertService.toastInfo('Information importante');
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-xl font-bold text-secondary-900 dark:text-gray-100 mb-4">
          🎨 Démonstration SweetAlert2
        </h2>
        <p className="text-secondary-600 dark:text-gray-400 mb-6">
          Testez les différentes modales et alertes personnalisées de l'application.
        </p>

        {/* Alertes de base */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-gray-100 mb-4">
            Alertes de base
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={showSuccessAlert}
              className="btn btn-success flex items-center justify-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Succès
            </button>
            
            <button
              onClick={showErrorAlert}
              className="btn btn-danger flex items-center justify-center"
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Erreur
            </button>
            
            <button
              onClick={showWarningAlert}
              className="btn btn-secondary flex items-center justify-center"
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Avertissement
            </button>
            
            <button
              onClick={showInfoAlert}
              className="btn btn-primary flex items-center justify-center"
            >
              <Info className="h-4 w-4 mr-2" />
              Information
            </button>
            
            <button
              onClick={showQuestionAlert}
              className="btn btn-secondary flex items-center justify-center"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Question
            </button>
            
            <button
              onClick={showConfirmDialog}
              className="btn btn-primary flex items-center justify-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirmation
            </button>
          </div>
        </div>

        {/* Modales personnalisées */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-gray-100 mb-4">
            Modales personnalisées
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={showDeleteConfirm}
              className="btn btn-danger flex items-center justify-center"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Suppression
            </button>
            
            <button
              onClick={showSaveConfirm}
              className="btn btn-success flex items-center justify-center"
            >
              <Save className="h-4 w-4 mr-2" />
              Sauvegarde
            </button>
            
            <button
              onClick={showCustomModal}
              className="btn btn-primary flex items-center justify-center"
            >
              <Info className="h-4 w-4 mr-2" />
              Modal HTML
            </button>
            
            <button
              onClick={showFormModal}
              className="btn btn-primary flex items-center justify-center"
            >
              <Send className="h-4 w-4 mr-2" />
              Formulaire
            </button>
            
            <button
              onClick={showLoadingModal}
              className="btn btn-secondary flex items-center justify-center"
            >
              <Settings className="h-4 w-4 mr-2" />
              Chargement
            </button>
          </div>
        </div>

        {/* Toasts */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-gray-100 mb-4">
            Notifications toast
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button
              onClick={showToastSuccess}
              className="btn btn-success flex items-center justify-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Toast Succès
            </button>
            
            <button
              onClick={showToastError}
              className="btn btn-danger flex items-center justify-center"
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Toast Erreur
            </button>
            
            <button
              onClick={showToastInfo}
              className="btn btn-primary flex items-center justify-center"
            >
              <Info className="h-4 w-4 mr-2" />
              Toast Info
            </button>
          </div>
        </div>

        {/* Informations */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            💡 Fonctionnalités
          </h4>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li>• Support du mode sombre automatique</li>
            <li>• Animations fluides et modernes</li>
            <li>• Validation de formulaires intégrée</li>
            <li>• Personnalisation complète des styles</li>
            <li>• Support des icônes et HTML</li>
            <li>• Notifications toast non-intrusives</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SweetAlertDemo;
